import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, FlaskConical, Mic, Square, Send, RotateCcw, Clock,
  ChevronRight, Check, Zap, User, Gauge, Pencil, Trash2,
} from "lucide-react";
import { voiceApi, streamAsset } from "@/api/mist";
import { useFounderGate } from "@/lib/useFounderGate";

const TEAL = "#00F5D4";

const TRAIN_SCRIPTS = [
  { id: 1, type: "Phonetic", text: "The quick brown fox jumps over the lazy dog near the vibrant quartz sphinx." },
  { id: 2, type: "Rhyme", text: "A flash of light across the night, a silent bird in steady flight, the echo fades out of sight." },
  { id: 3, type: "Sibilance", text: "She sells seashells by the seashore while sitting silently on the silver sand." },
  { id: 4, type: "Technical", text: "We must act deliberately. The parameters of the database require immediate structural balance." },
  { id: 5, type: "Dynamics", text: "Wait, look over there! Did you see how fast that engine started moving? Unbelievable!" },
  { id: 6, type: "Resonance", text: "Deep within the stone cavern, a slow, low hum rumbled through the ancient foundation." },
];

/* Jump straight into the Mist Stitcher composer with the take pre-staged. */
function goToStitcher() {
  const p = new URLSearchParams(window.location.search);
  p.set("studio", "stitcher");
  p.set("view", "stitcher");
  window.history.pushState({ studio: "stitcher" }, "", `${window.location.pathname}?${p}`);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

async function postToReel({ take_id, asset_id }) {
  try {
    await voiceApi.postToReel({ take_id, asset_id });
    toast.success("Staged in Mist Stitcher");
    goToStitcher();
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Could not post to reel");
  }
}

function StudioShell({ title, Icon, color, onBack, children }) {
  return (
    <div className="h-full w-full flex flex-col" data-testid={`voice-shell-${title}`}>
      <div className="shrink-0 px-4 pt-3 pb-1">
        <button onClick={onBack} data-testid="music-tile-back" className="flex items-center gap-1.5 font-mono text-[9px] font-black uppercase tracking-widest text-white/60">
          <ArrowLeft size={13} /> Music Studios
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-28 pt-2 space-y-4">
        <div className="flex items-center gap-2.5">
          <span className="w-11 h-11 rounded-xl border-2 border-[#2a2a2a] grid place-items-center" style={{ backgroundColor: color }}>
            <Icon size={22} strokeWidth={2.2} className="text-white" />
          </span>
          <div>
            <p className="font-serif text-2xl text-white leading-none">{title}</p>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

/* Streams a music vault asset into an <audio> element. */
function TakeAudio({ assetId }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    if (assetId) streamAsset(assetId).then((u) => { url = u; setSrc(u); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [assetId]);
  if (!src) return <Loader2 size={16} className="text-white/40 animate-spin" />;
  return <audio src={src} controls className="w-full" data-testid="voice-take-audio" />;
}

/* ========================= MY CLONED VOICES MANAGER ===================== */
function VoiceManager({ refreshKey }) {
  const [voices, setVoices] = useState(null);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");

  const load = () => voiceApi.clonedVoices().then((d) => setVoices(d.voices || [])).catch(() => setVoices([]));
  useEffect(() => { load(); }, [refreshKey]);

  const rename = async (v) => {
    const name = draft.trim();
    if (!name) return;
    try { await voiceApi.renameCloned(v.voice_id, name); setEditing(null); load(); toast.success("Renamed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Rename failed"); }
  };
  const remove = async (v) => {
    if (!window.confirm(`Delete "${v.name}"? This can't be undone.`)) return;
    try { await voiceApi.deleteCloned(v.voice_id); load(); toast("Voice deleted"); }
    catch { toast.error("Delete failed"); }
  };

  if (voices == null) return <div className="flex items-center gap-2 font-mono text-[10px] text-white/50"><Loader2 size={13} className="animate-spin" /> Loading voices…</div>;

  return (
    <div className="border-2 border-[#2a2a2a] rounded-2xl bg-[#141414] p-3 shadow-[3px_3px_0px_#1A1A1A]" data-testid="voice-manager">
      <p className="font-mono text-[9px] font-black uppercase tracking-[0.16em] text-white/60 mb-2">My Cloned Voices</p>
      {voices.length === 0 ? (
        <p className="font-mono text-[10px] text-white/45">No cloned voices yet — record & train one below to add it here.</p>
      ) : (
        <div className="space-y-2" data-testid="voice-manager-list">
          {voices.map((v) => (
            <div key={v.voice_id} className="flex items-center gap-2 border-2 border-[#2a2a2a]/15 rounded-xl px-2.5 py-1.5" data-testid={`cloned-voice-${v.voice_id}`}>
              <span className="w-7 h-7 rounded-full border-2 border-[#2a2a2a] grid place-items-center bg-[#00F5D4] shrink-0"><User size={13} className="text-white" /></span>
              {editing === v.voice_id ? (
                <>
                  <input value={draft} onChange={(e) => setDraft(e.target.value.slice(0, 60))} autoFocus data-testid="cloned-rename-input"
                    className="flex-1 min-w-0 border-2 border-black bg-[#141414] text-white px-2 py-1 font-mono text-[11px] outline-none" />
                  <button onClick={() => rename(v)} data-testid={`cloned-save-${v.voice_id}`} className="w-7 h-7 grid place-items-center border-2 border-black bg-[#00F5D4] rounded active:scale-95"><Check size={13} /></button>
                </>
              ) : (
                <>
                  <span className="flex-1 min-w-0 font-mono text-[11px] font-bold text-white truncate">{v.name}</span>
                  <button onClick={() => { setEditing(v.voice_id); setDraft(v.name); }} data-testid={`cloned-rename-${v.voice_id}`} className="w-7 h-7 grid place-items-center border-2 border-[#2a2a2a] bg-[#141414] rounded active:scale-95"><Pencil size={12} /></button>
                  <button onClick={() => remove(v)} data-testid={`cloned-delete-${v.voice_id}`} className="w-7 h-7 grid place-items-center border-2 border-black bg-red-500 text-white rounded active:scale-95"><Trash2 size={12} /></button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================== AI VOICE LAB ============================== */
const EMOTIONS = [
  { k: "HAPPY", c: "#FFE66D" }, { k: "INTENSE", c: "#FF6B6B" }, { k: "FORMAL", c: "#A4DCFA" },
  { k: "CALM", c: "#9EFFB8" }, { k: "DRAMATIC", c: "#E8D3FF" }, { k: "NATURAL", c: "#00F5D4" },
];

export function VoiceLab({ onBack }) {
  const [voices, setVoices] = useState(null);
  const [voiceId, setVoiceId] = useState("");
  const [text, setText] = useState("");
  const [emotion, setEmotion] = useState("NATURAL");
  const [speed, setSpeed] = useState(1.0);
  const [busy, setBusy] = useState(false);
  const [take, setTake] = useState(null);
  const [mode, setMode] = useState("synth");
  const [cloneTick, setCloneTick] = useState(0);

  // Speech-to-Speech accent morph (founder-only)
  const { isFounder } = useFounderGate();
  const [labMode, setLabMode] = useState("tts"); // tts | s2s
  const [accents, setAccents] = useState([]);
  const [accentId, setAccentId] = useState("italian_dramatic");
  const [s2sFile, setS2sFile] = useState(null);
  const [recing, setRecing] = useState(false);
  const s2sRecRef = useRef(null);
  const s2sChunksRef = useRef([]);

  useEffect(() => {
    if (!isFounder) return;
    voiceApi.s2sAccents().then((d) => setAccents(d.accents || [])).catch(() => {});
  }, [isFounder]);

  const startS2sRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      s2sChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) s2sChunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(s2sChunksRef.current, { type: rec.mimeType || "audio/webm" });
        setS2sFile(new File([blob], "s2s.webm", { type: blob.type }));
        stream.getTracks().forEach((t) => t.stop());
      };
      s2sRecRef.current = rec; rec.start(); setRecing(true);
    } catch { toast.error("Mic access denied"); }
  };
  const stopS2sRec = () => { try { s2sRecRef.current?.stop(); } catch {} setRecing(false); };

  const doMorph = async () => {
    if (!s2sFile) { toast.error("Record or upload a voice clip first"); return; }
    setBusy(true); setTake(null);
    try {
      const fd = new FormData();
      fd.append("file", s2sFile, s2sFile.name || "s2s.webm");
      fd.append("accent_id", accentId);
      fd.append("emotion", emotion);
      fd.append("speed", String(speed));
      const r = await voiceApi.s2sMorph(fd);
      setTake(r.take);
      toast.success("Voice morphed ✓");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Voice morph failed");
    } finally { setBusy(false); }
  };

  useEffect(() => {
    voiceApi.voices().then((d) => {
      setVoices(d.voices || []);
      if (d.voices?.[0]) setVoiceId(d.voices[0].voice_id);
    }).catch(() => setVoices([]));
  }, []);

  const synth = async () => {
    if (!text.trim()) { toast.error("Enter a script to synthesize"); return; }
    setBusy(true); setTake(null);
    try {
      const r = await voiceApi.synthesize({
        text, voice_id: voiceId, emotion, speed,
        voice_name: voices?.find((v) => v.voice_id === voiceId)?.name,
      });
      setTake(r.take);
      toast.success(`Voice take ready · ${r.engine}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Synthesis failed");
    } finally { setBusy(false); }
  };

  if (mode === "train") {
    return (
      <StudioShell title="Voice Cloning" Icon={FlaskConical} color="#A4DCFA" onBack={() => setMode("synth")}>
        <VoiceManager refreshKey={cloneTick} />
        <VoiceTrainer onCloned={() => setCloneTick((t) => t + 1)} />
      </StudioShell>
    );
  }

  return (
    <StudioShell title="AI Voice Lab" Icon={FlaskConical} color="#A4DCFA" onBack={onBack}>
      {isFounder && (
        <div className="grid grid-cols-2 gap-2" data-testid="lab-mode-toggle">
          {[["tts", "Text → Speech"], ["s2s", "Speech → Speech"]].map(([m, lbl]) => (
            <button key={m} data-testid={`lab-mode-${m}`} onClick={() => { setLabMode(m); setTake(null); }}
              className="font-mono text-[10px] font-black uppercase tracking-widest py-2.5 rounded-xl border-2 border-black transition-all"
              style={{ backgroundColor: labMode === m ? "#00F5D4" : "#141414", color: labMode === m ? "#1A1A1A" : "rgba(255,255,255,0.7)", boxShadow: labMode === m ? "none" : "3px 3px 0px #1A1A1A" }}>{lbl}</button>
          ))}
        </div>
      )}
      {labMode === "tts" && (<>
      {/* 1 · VOCALIST SELECTION TRACK */}
      <div>
        <p className="font-mono text-[9px] font-black uppercase tracking-[0.18em] text-white/60 mb-2">Choose Virtual Vocalist</p>
        {voices == null ? (
          <div className="flex items-center gap-2 font-mono text-[10px] text-white/50"><Loader2 size={13} className="animate-spin" /> Loading vocalists…</div>
        ) : (
          <div className="flex gap-2.5 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1" data-testid="vocalist-track">
            {voices.map((v) => {
              const active = v.voice_id === voiceId;
              return (
                <button key={v.voice_id} onClick={() => setVoiceId(v.voice_id)} data-testid={`vocalist-${v.voice_id}`}
                  className="shrink-0 w-[92px] rounded-2xl bg-[#141414] p-2.5 flex flex-col items-center gap-1.5 transition-all"
                  style={{
                    border: `3px solid ${active ? "#00F5D4" : "#1A1A1A"}`,
                    boxShadow: active ? "0 0 0 2px #1A1A1A, 0 0 16px 2px #00F5D4" : "3px 3px 0px #1A1A1A",
                  }}>
                  <span className="w-11 h-11 rounded-full border-2 border-[#2a2a2a] grid place-items-center"
                    style={{ backgroundColor: active ? "#00F5D4" : "#F4EBDD" }}>
                    <User size={20} strokeWidth={2.4} className="text-white" />
                  </span>
                  <span className="font-mono text-[8.5px] font-black uppercase tracking-tight text-white text-center leading-tight line-clamp-2 h-[22px]">{v.name}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* 2 · SPEECH SCRIPT */}
      <div className="border-4 border-[#2a2a2a] rounded-[18px] bg-[#141414] p-3 shadow-[4px_4px_0px_#1A1A1A]">
        <p className="font-mono text-[9px] font-black uppercase tracking-[0.16em] text-white/60 mb-1.5">Speech Script to Generate</p>
        <textarea value={text} onChange={(e) => setText(e.target.value.slice(0, 1500))} rows={4} data-testid="voice-lab-text"
          placeholder="Type what your vocalist should say…"
          className="w-full bg-black text-white placeholder:text-white/40 border-2 border-black px-3 py-2 font-mono text-xs outline-none focus:ring-2 focus:ring-gold resize-none break-words" />
      </div>
      </>)}

      {labMode === "s2s" && (
        <div className="space-y-3" data-testid="s2s-panel">
          <div>
            <p className="font-mono text-[9px] font-black uppercase tracking-[0.18em] text-white/60 mb-2">Target Accent</p>
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="s2s-accent-track">
              {accents.map((a) => {
                const on = a.id === accentId;
                return (
                  <button key={a.id} onClick={() => setAccentId(a.id)} data-testid={`s2s-accent-${a.id}`}
                    className="shrink-0 font-mono text-[9px] font-black uppercase tracking-wide px-3 py-2 rounded-xl border-2 border-black"
                    style={{ backgroundColor: on ? "#00F5D4" : "#FFFFFF", color: "#1A1A1A", boxShadow: on ? "none" : "3px 3px 0px #1A1A1A" }}>{a.name}</button>
                );
              })}
            </div>
          </div>
          <div className="border-4 border-[#2a2a2a] rounded-[18px] bg-[#141414] p-3 shadow-[4px_4px_0px_#1A1A1A] space-y-2.5">
            <p className="font-mono text-[9px] font-black uppercase tracking-[0.16em] text-white/60">Your Performance (record or upload)</p>
            <div className="flex gap-2">
              {!recing ? (
                <button onClick={startS2sRec} data-testid="s2s-record" className="flex-1 flex items-center justify-center gap-2 bg-[#00F5D4] text-black font-black font-mono text-[11px] uppercase tracking-widest py-2.5 border-2 border-black shadow-[3px_3px_0px_#000]"><Mic size={14} /> Record</button>
              ) : (
                <button onClick={stopS2sRec} data-testid="s2s-stop" className="flex-1 flex items-center justify-center gap-2 bg-[#FF6B6B] text-white font-black font-mono text-[11px] uppercase tracking-widest py-2.5 border-2 border-black animate-pulse"><Square size={14} /> Stop</button>
              )}
              <label data-testid="s2s-upload" className="flex-1 flex items-center justify-center gap-2 cursor-pointer bg-[#141414] text-white/80 font-black font-mono text-[11px] uppercase tracking-widest py-2.5 border-2 border-dashed border-white/25">
                Upload
                <input type="file" accept="audio/*" className="hidden" data-testid="s2s-file-input" onChange={(e) => setS2sFile(e.target.files?.[0] || null)} />
              </label>
            </div>
            {s2sFile && <p className="font-mono text-[9px] text-[#00F5D4] truncate" data-testid="s2s-file-name">✓ {s2sFile.name}</p>}
            <p className="font-mono text-[9px] leading-relaxed text-white/45" data-testid="s2s-hint">Transcribes your clip, then re-speaks your words in the chosen accent voice (Fish Audio). Note: this regenerates speech — exact timing isn't preserved.</p>
          </div>
        </div>
      )}

      {/* 3 · EMOTION ENGINE */}
      <div>
        <p className="font-mono text-[9px] font-black uppercase tracking-[0.18em] text-white/60 mb-2">Emotion Engine</p>
        <div className="grid grid-cols-3 gap-2" data-testid="emotion-grid">
          {EMOTIONS.map((e) => {
            const on = emotion === e.k;
            return (
              <button key={e.k} onClick={() => setEmotion(e.k)} data-testid={`emotion-${e.k.toLowerCase()}`}
                className="font-mono text-[10px] font-black uppercase tracking-wide py-3 rounded-xl border-2 border-black transition-all active:translate-y-[2px]"
                style={{
                  backgroundColor: on ? e.c : "#FFFFFF",
                  color: "#1A1A1A",
                  boxShadow: on ? "none" : "3px 3px 0px #1A1A1A",
                  transform: on ? "translate(2px,2px)" : "none",
                }}>{e.k}</button>
            );
          })}
        </div>
      </div>

      {/* 4 · SPEED & TEMPO */}
      <div className="border-2 border-[#2a2a2a] rounded-2xl bg-[#141414] p-3 shadow-[3px_3px_0px_#1A1A1A]">
        <div className="flex items-center justify-between mb-2">
          <span className="flex items-center gap-1.5 font-mono text-[9px] font-black uppercase tracking-[0.16em] text-white/60"><Gauge size={13} /> Speed &amp; Tempo</span>
          <span className="font-serif text-lg text-white leading-none" data-testid="speed-value">{speed.toFixed(2)}x</span>
        </div>
        <input type="range" min="0.5" max="2" step="0.05" value={speed} onChange={(e) => setSpeed(parseFloat(e.target.value))}
          data-testid="speed-slider" className="voice-lab-slider w-full" />
        <div className="flex justify-between font-mono text-[8px] text-white/40 mt-1"><span>0.5x</span><span>2.0x</span></div>
      </div>

      {/* 6 · AUDIO INJECTION (above trigger) */}
      {take && (
        <div className="border-4 border-[#00F5D4] bg-[#141414] p-3 rounded-[18px] shadow-[4px_4px_0px_#000] space-y-2.5" data-testid="voice-lab-result">
          <span className="font-mono text-[8px] uppercase tracking-widest bg-charcoal text-gold px-2 py-0.5 rounded-full">{take.engine}</span>
          <TakeAudio assetId={take.asset_id} />
          <button onClick={() => postToReel({ take_id: take.id })} data-testid="voice-lab-post-reel"
            className="w-full flex items-center justify-center gap-2 bg-[#00F5D4] text-white font-black font-mono text-[11px] uppercase tracking-widest py-2.5 border-2 border-black shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
            <Send size={14} /> Post to Reel
          </button>
        </div>
      )}

      {/* 5 · HIGH-IMPACT ACTION TRIGGER */}
      <div className="pt-1 pb-1">
        <button onClick={labMode === "s2s" ? doMorph : synth} disabled={busy} data-testid={labMode === "s2s" ? "s2s-morph" : "voice-lab-synth"}
          className="w-full flex items-center justify-center gap-2.5 bg-[#00F5D4] text-white font-black font-mono text-sm uppercase tracking-[0.12em] py-4 rounded-xl border-[3px] border-black shadow-[6px_6px_0px_#000] active:translate-x-[4px] active:translate-y-[4px] active:shadow-none disabled:opacity-60 transition-transform">
          {busy ? <Loader2 size={18} className="animate-spin" /> : <Zap size={18} strokeWidth={2.6} />} {busy ? (labMode === "s2s" ? "Morphing…" : "Synthesizing…") : (labMode === "s2s" ? "Morph Voice" : "Synthesize AI Voice")}
        </button>
        <button onClick={() => setMode("train")} data-testid="voice-lab-train-link"
          className="w-full mt-2 font-mono text-[9px] font-black uppercase tracking-[0.14em] text-white/50 hover:text-white">
          Clone your own voice →
        </button>
      </div>
    </StudioShell>
  );
}

/* =========================== VOICE TRAINER (IVC) ======================== */
function VoiceTrainer({ onCloned }) {
  const [step, setStep] = useState(0);
  const [clips, setClips] = useState({});           // { scriptId: Blob }
  const [recording, setRecording] = useState(false);
  const [testText, setTestText] = useState("Hello world, testing my new cloned digital voice-print.");
  const [ttsUrl, setTtsUrl] = useState(null);
  const [result, setResult] = useState(null);       // { cloned, engine }
  const [busy, setBusy] = useState(false);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  useEffect(() => () => { streamRef.current?.getTracks().forEach((t) => t.stop()); }, []);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const rec = new MediaRecorder(stream);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      const sid = TRAIN_SCRIPTS[step].id;
      rec.onstop = () => {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        setClips((prev) => ({ ...prev, [sid]: blob }));
      };
      rec.start();
      setRecording(true);
    } catch (e) {
      toast.error("Microphone permission denied");
    }
  };
  const stop = () => {
    if (recorderRef.current && recording) { recorderRef.current.stop(); setRecording(false); }
  };

  const done = Object.keys(clips).length;
  const minMet = done >= 4;

  const compile = async () => {
    setBusy(true); setTtsUrl(null); setResult(null);
    try {
      const fd = new FormData();
      Object.keys(clips).forEach((sid) => fd.append(`file_script_${sid}`, clips[sid], `script_${sid}.webm`));
      fd.append("test_string", testText);
      const d = await voiceApi.cloneAndTest(fd);
      setTtsUrl(d.audio_url);
      setResult({ cloned: d.cloned, engine: d.engine });
      if (d.cloned) onCloned?.();
      toast.success(d.cloned ? "Voice cloned — test ready" : "Test rendered (fallback voice)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Voice training failed");
    } finally { setBusy(false); }
  };

  const sc = TRAIN_SCRIPTS[step];
  return (
    <div className="space-y-4" data-testid="voice-trainer">
      <div className="border-2 border-dashed border-[#2a2a2a]/30 rounded-xl p-2.5">
        <p className="font-mono text-[10px] text-white/60 leading-relaxed">Read at least <b>4</b> scripts to synthesize your custom voice profile.</p>
      </div>

      {/* progress bars */}
      <div className="flex gap-1" data-testid="trainer-progress">
        {TRAIN_SCRIPTS.map((s, i) => (
          <button key={s.id} onClick={() => setStep(i)} data-testid={`trainer-step-${s.id}`}
            className="flex-1 h-1.5 rounded-full border border-[#2a2a2a]"
            style={{ background: step === i ? TEAL : clips[s.id] ? "#9EFFB8" : "#fff" }} />
        ))}
      </div>

      {/* script reader */}
      <div className="border-4 border-[#2a2a2a] rounded-[20px] bg-[#141414] p-4 shadow-[4px_4px_0px_#1A1A1A] space-y-3" data-testid="trainer-card">
        <span className="font-mono text-[9px] font-black uppercase tracking-[0.16em] text-white/50">Script {step + 1}/6 · {sc.type}</span>
        <p className="font-serif text-lg text-white leading-snug min-h-[64px]">“{sc.text}”</p>
        <div className="flex gap-2">
          {!recording ? (
            <button onClick={start} data-testid="trainer-record"
              className="flex-1 flex items-center justify-center gap-2 bg-[#FF6B6B] text-white font-black font-mono text-[11px] uppercase tracking-widest py-2.5 border-2 border-black shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
              <Mic size={14} /> Record
            </button>
          ) : (
            <button onClick={stop} data-testid="trainer-stop"
              className="flex-1 flex items-center justify-center gap-2 bg-charcoal text-white font-black font-mono text-[11px] uppercase tracking-widest py-2.5 border-2 border-black shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
              <Square size={14} /> Stop
            </button>
          )}
          <button onClick={() => setStep((p) => Math.min(TRAIN_SCRIPTS.length - 1, p + 1))} disabled={step === TRAIN_SCRIPTS.length - 1} data-testid="trainer-next"
            className="px-4 flex items-center justify-center bg-[#141414] border-2 border-[#2a2a2a] shadow-[3px_3px_0px_#000] disabled:opacity-40 active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
            <ChevronRight size={16} />
          </button>
        </div>
        {clips[sc.id] && (
          <div className="flex items-center gap-1.5 font-mono text-[10px] text-[#1a9e5f]" data-testid="trainer-captured">
            <Check size={13} /> Audio captured for this script.
          </div>
        )}
      </div>

      {/* test bench */}
      <div className="border-4 border-[#00F5D4] bg-black rounded-[20px] p-4 shadow-[4px_4px_0px_#000] space-y-3" data-testid="trainer-bench">
        <span className="font-mono text-[9px] font-black uppercase tracking-[0.16em] text-white/60">Live Synthesis Test · {done}/4 required</span>
        <input value={testText} onChange={(e) => setTestText(e.target.value.slice(0, 300))} data-testid="trainer-test-text"
          className="w-full border-2 border-black bg-[#141414] text-white placeholder:text-white/40 px-3 py-2 font-mono text-xs outline-none focus:ring-2 focus:ring-gold" />
        <button onClick={compile} disabled={!minMet || busy} data-testid="trainer-compile"
          className="w-full flex items-center justify-center gap-2 font-black font-mono text-xs uppercase tracking-widest py-3 border-2 border-black shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-50"
          style={{ background: minMet ? TEAL : "#e5e5e5", color: minMet ? "#1A1A1A" : "#999" }}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Zap size={15} />} {busy ? "Synthesizing Array…" : "Test Speech Output"}
        </button>
        {ttsUrl && (
          <div className="space-y-1.5" data-testid="trainer-result">
            <span className="font-mono text-[8px] uppercase tracking-widest bg-charcoal text-gold px-2 py-0.5 rounded-full">{result?.cloned ? "cloned voice" : result?.engine}</span>
            <audio src={ttsUrl} controls className="w-full" data-testid="trainer-audio" />
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================= RECORDING CRIB ============================ */
// Multi-tonal cinematic capture script — line breaks guide pacing, exclamation
// emphasis and numeric pauses across the ~15s take.
const CRIB_SCRIPT_LINES = [
  "Three... Two... One... Go!",
  "The grid is officially live, and the baseline is dropping hard!",
  "Wait—did you hear that? Wow!",
  "The numbers are skyrocketing out of nowhere!",
  "In a world built on code and rhythm, we are locking down the final frontier.",
  "Clear, crisp, and completely frozen into production status.",
  "Stage Four is sealed.",
];

export function RecordingCrib({ onBack }) {
  const [state, setState] = useState("idle"); // idle | recording | uploading
  const [secs, setSecs] = useState(0);
  const [take, setTake] = useState(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const stop = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
    clearInterval(timerRef.current);
  };

  useEffect(() => () => { clearInterval(timerRef.current); streamRef.current?.getTracks().forEach((t) => t.stop()); }, []);

  const start = async () => {
    setTake(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const rec = new MediaRecorder(stream);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        const dur = secs;
        setState("uploading");
        try {
          const file = new File([blob], "crib.webm", { type: blob.type });
          const r = await voiceApi.cribIngest(file, dur, "Crib take");
          setTake(r.take);
          toast.success("Recording saved to Voice Vault");
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Upload failed");
        } finally { setState("idle"); setSecs(0); }
      };
      rec.start();
      setState("recording");
      setSecs(0);
      timerRef.current = setInterval(() => {
        setSecs((s) => {
          if (s + 1 >= 10) { stop(); return 10; }
          return s + 1;
        });
      }, 1000);
    } catch (e) {
      toast.error("Microphone permission denied");
    }
  };

  return (
    <StudioShell title="Recording Crib" Icon={Mic} color="#FFC9A3" onBack={onBack}>
      <div className="border-2 border-dashed border-[#2a2a2a]/30 rounded-xl p-2.5">
        <p className="font-mono text-[10px] text-white/60 leading-relaxed">Tap record for a quick <b>10-second</b> mic capture. Read the script below aloud — it auto-stops and saves to your Voice Vault.</p>
      </div>

      {/* Read-aloud cinematic script marquee — line breaks guide pacing */}
      <div className="border-4 border-[#FF6B6B] rounded-[18px] bg-[#141414] p-3.5 shadow-[4px_4px_0px_#1A1A1A]" data-testid="crib-script-box">
        <p className="font-mono text-[8.5px] font-black uppercase tracking-[0.18em] text-[#FF6B6B] mb-2">Read Aloud · ~15s</p>
        <div className="space-y-1.5" data-testid="crib-script-text">
          {CRIB_SCRIPT_LINES.map((line, i) => (
            <p key={i} className="font-serif text-[15px] leading-snug text-white">{line}</p>
          ))}
        </div>
      </div>

      <div className="border-4 border-[#2a2a2a] rounded-[24px] bg-[#141414] p-6 shadow-[4px_4px_0px_#1A1A1A] flex flex-col items-center gap-4" data-testid="crib-panel">
        <div className="relative w-28 h-28 rounded-full border-4 border-[#2a2a2a] grid place-items-center"
          style={{ backgroundColor: state === "recording" ? "#FF6B6B" : "#FFC9A3" }}>
          <span className="font-serif text-3xl text-white">{String(secs).padStart(2, "0")}s</span>
          {state === "recording" && <span className="absolute inset-0 rounded-full border-4 border-[#FF6B6B] animate-ping" />}
        </div>

        {state === "uploading" ? (
          <div className="flex items-center gap-2 font-mono text-[11px] text-white/60"><Loader2 size={16} className="animate-spin" /> Saving…</div>
        ) : state === "recording" ? (
          <button onClick={stop} data-testid="crib-stop"
            className="flex items-center gap-2 bg-charcoal text-white font-black font-mono text-xs uppercase tracking-widest px-6 py-3 rounded-full border-2 border-black shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
            <Square size={15} /> Stop
          </button>
        ) : (
          <button onClick={start} data-testid="crib-record"
            className="flex items-center gap-2 bg-[#FF6B6B] text-white font-black font-mono text-xs uppercase tracking-widest px-6 py-3 rounded-full border-2 border-black shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
            <Mic size={15} /> Record
          </button>
        )}
      </div>

      {take && (
        <div className="border-4 border-[#FFC9A3] bg-[#141414] p-3 rounded-[20px] shadow-[4px_4px_0px_#000] space-y-3" data-testid="crib-result">
          <TakeAudio assetId={take.asset_id} />
          <button onClick={() => postToReel({ take_id: take.id })} data-testid="crib-post-reel"
            className="w-full flex items-center justify-center gap-2 bg-[#00F5D4] text-white font-black font-mono text-[11px] uppercase tracking-widest py-2.5 border-2 border-black shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
            <Send size={14} /> Post to Reel
          </button>
        </div>
      )}
    </StudioShell>
  );
}

/* =========================== VOICE VAULT ARCHIVE ========================= */
export function VoiceVault({ onBack }) {
  const [takes, setTakes] = useState(null);
  const load = () => voiceApi.vault().then((d) => setTakes(d.takes || [])).catch(() => setTakes([]));
  useEffect(() => { load(); }, []);

  return (
    <StudioShell title="Voice Vault" Icon={Clock} color="#E8D3FF" onBack={onBack}>
      <div className="flex items-center justify-between">
        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/50">Saved takes</p>
        <button onClick={load} data-testid="vault-refresh" className="flex items-center gap-1 font-mono text-[9px] font-black uppercase tracking-widest text-white/60">
          <RotateCcw size={12} /> Refresh
        </button>
      </div>

      {takes == null ? (
        <div className="flex items-center gap-2 font-mono text-[11px] text-white/50 py-4"><Loader2 size={16} className="animate-spin" /> Loading…</div>
      ) : takes.length === 0 ? (
        <div className="border-2 border-dashed border-[#2a2a2a]/30 rounded-xl p-6 text-center">
          <p className="font-mono text-[11px] text-white/50">No voice takes yet. Synthesize one in the AI Voice Lab or record in the Crib.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="vault-list">
          {takes.map((t) => (
            <div key={t.id} className="border-2 border-[#2a2a2a] rounded-2xl bg-[#141414] p-3 shadow-[3px_3px_0px_#1A1A1A] space-y-2" data-testid={`vault-take-${t.id}`}>
              <div className="flex items-center gap-2">
                <span className="font-mono text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border-2 border-[#2a2a2a]"
                  style={{ backgroundColor: t.kind === "recording" ? "#FFC9A3" : "#A4DCFA" }}>{t.kind}</span>
                <span className="font-mono text-[8px] uppercase tracking-widest text-white/50 truncate flex-1">{(t.created_at || "").slice(0, 16).replace("T", " ")}</span>
              </div>
              {t.text && <p className="font-mono text-[10px] text-white/70 line-clamp-2">{t.text}</p>}
              <TakeAudio assetId={t.asset_id} />
              <button onClick={() => postToReel({ take_id: t.id })} data-testid={`vault-post-reel-${t.id}`}
                className="w-full flex items-center justify-center gap-2 bg-[#00F5D4] text-white font-black font-mono text-[10px] uppercase tracking-widest py-2 border-2 border-black shadow-[2px_2px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
                <Send size={13} /> Post to Reel
              </button>
            </div>
          ))}
        </div>
      )}
    </StudioShell>
  );
}
